import { moneyFormat } from 'helpers/NumberFormat';
import { Order, OrderProduct } from 'types/order';
import { Complement, ComplementCategory, OrderProductAdditional } from 'types/product';
import { RestaurantConfig } from 'types/restaurant';
import { v4 } from 'uuid';

type Prices = {
  complement: number;
  complementAdditional: number;
  tastes: number[];
};

export function addProduct(order: Order, product: OrderProduct, amount: number): OrderProduct {
  const price = getProductPrice(product);

  const additionalPrice = getAdditionalPrice(product.additional as OrderProductAdditional[]);

  const complementsPrice = product.category.is_pizza
    ? getPizzaComplementsPrice(product.complement_categories, order.settings)
    : getComplementPrice(product.complement_categories);

  const finalPrice = (price + additionalPrice + complementsPrice) * amount;

  return {
    ...product,
    ready: true,
    additional: product.additional as OrderProductAdditional[],
    uid: v4(),
    amount,
    product_price: price,
    price: price + additionalPrice + complementsPrice,
    final_price: finalPrice,
    complementsPrice,
    additionalPrice,
    formattedProductPrice: moneyFormat(price),
    formattedPrice: moneyFormat(price + complementsPrice),
    formattedFinalPrice: moneyFormat(finalPrice),
    promotion_id: null,
    product_id: product?.product_id ?? product.id,
    annotation: product.annotation ?? null,
  };
}

function getAdditionalPrice(additional: OrderProductAdditional[]) {
  return additional
    .filter(item => item.selected)
    .reduce((sum, additional) => {
      return sum + additional.price * additional.amount;
    }, 0);
}

function getProductPrice(product: OrderProduct) {
  if (product.promotion_activated && product.special_price) {
    return product.special_price;
  }

  return product.product_price ?? product.price;
}

function getComplementPrice(complementCategories: ComplementCategory[]): number {
  let price = 0;

  complementCategories.forEach(category => {
    price += category.complements
      .filter(item => item.selected && item.price)
      .reduce((sum, complement) => sum + (complement.price as number), 0);
  });

  return price;
}

export function getPizzaComplementsPrice(
  complementCategories: ComplementCategory[],
  settings?: RestaurantConfig | null
): number {
  const prices: Prices = {
    complement: 0,
    complementAdditional: 0,
    tastes: [],
  };

  complementCategories.forEach(category => {
    category.complements
      .filter(item => item.selected)
      .forEach(complement => {
        complement.prices
          .filter(item => item.selected && item.price)
          .forEach(item => {
            if (category.is_pizza_taste) {
              prices.tastes.push(item.price as number);
              return;
            }

            prices.complement += item.price as number;
          });

        prices.complementAdditional += getComplementAdditionalPrice(complement);
      });
  });

  let tastePrice = 0;

  if (settings?.pizza_calculate === 'average_value') {
    tastePrice = prices.tastes.reduce((sum, item) => sum + item, 0) / prices.tastes.length;
  }

  if (settings?.pizza_calculate === 'higher_value') {
    tastePrice = Math.max.apply(Math, prices.tastes);
  }

  return prices.complement + tastePrice + prices.complementAdditional;
}

function getComplementAdditionalPrice(complement: Complement): number {
  let value = 0;

  complement.additional
    .filter(item => item.selected)
    .forEach(additional => {
      additional.prices
        .filter(item => item.selected && item.price)
        .forEach(price => (value = (price.price as number) + value));
    });

  return value;
}

export function getComplementsPrice(product: OrderProduct, settings?: RestaurantConfig | null) {
  return product.category.is_pizza
    ? getPizzaComplementsPrice(product.complement_categories, settings)
    : getComplementPrice(product.complement_categories);
}
